import { Plane, Hotel, Utensils, Ticket, Bus, Globe } from "lucide-react";
import { asRange, formatRange } from "@/lib/api";

const Row = ({ icon: Icon, label, value, accent = "jade" }) => {
    const r = asRange(value);
    return (
        <div className="flex items-center justify-between border-b border-jade/10 py-3 last:border-0">
            <span className="flex items-center gap-2 text-sm text-moss">
                <span className={`grid h-8 w-8 place-items-center rounded-full ${accent === "terracotta" ? "bg-terracotta/10 text-terracotta" : "bg-jade/10 text-jade"}`}>
                    <Icon size={15} />
                </span>
                {label}
            </span>
            <span className="text-right">
                <span className="block font-display text-base text-ink leading-tight">{formatRange(value, { compact: true })}</span>
                {r.low !== r.high && <span className="block text-[10px] text-moss/70">avg ${r.avg.toLocaleString()}</span>}
            </span>
        </div>
    );
};

export const CostBreakdown = ({ data, days, travelers, currency, fx }) => {
    if (!data) return null;
    const total = asRange(data.total_estimated);
    const showRange = total.low !== total.high;
    const localCurrencyDisplay = currency && fx && currency !== "USD"
        ? `${(total.avg * fx).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`
        : null;

    return (
        <aside className="glass sticky top-24 rounded-3xl p-6 md:p-8" data-testid="cost-breakdown">
            <p className="overline">Realistic trip cost</p>
            {showRange ? (
                <>
                    <h3 className="mt-2 font-display text-4xl leading-none text-ink">
                        ${total.low.toLocaleString()}<span className="text-moss"> – </span>${total.high.toLocaleString()}
                    </h3>
                    <p className="mt-1 text-xs text-moss">avg <b className="text-terracotta">${total.avg.toLocaleString()}</b> for {travelers} traveler{travelers > 1 ? "s" : ""} · {days} day{days > 1 ? "s" : ""}</p>
                </>
            ) : (
                <>
                    <h3 className="mt-2 font-display text-5xl leading-none text-ink">${total.avg.toLocaleString()}</h3>
                    <p className="mt-2 text-sm text-moss">for {travelers} traveler{travelers > 1 ? "s" : ""} · {days} day{days > 1 ? "s" : ""}</p>
                </>
            )}

            {localCurrencyDisplay && (
                <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-jade/8 px-3 py-1 text-xs text-jade">
                    <Globe size={11} /> ≈ {localCurrencyDisplay}
                </p>
            )}

            <div className="mt-6">
                <Row icon={Plane} label="Flights / person" value={data.flights_per_person} accent="terracotta" />
                <Row icon={Hotel} label="Hotel / night" value={data.hotel_per_night} />
                <Row icon={Utensils} label="Food / day / person" value={data.food_per_day_per_person} accent="terracotta" />
                <Row icon={Ticket} label="Activities / day / person" value={data.activities_per_day_per_person} />
                <Row icon={Bus} label="Transport / day" value={data.transport_per_day} accent="terracotta" />
            </div>
        </aside>
    );
};

export default CostBreakdown;
